import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Text, Button, Switch, Row } from '@nextui-org/react';
import { useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";

import { mstore } from "../../store";
import checkAccess from '../../lib/checkAccess';
import { fetchWithJson } from "../../lib/api";
import endpoint from "../../config/endpoint.json";

import { error, success } from "../../lib/userAlerts";
import PostUploadModal from "../../components/NewsFeedComponent/PostUploadModal";
import LinkPreview from "../../components/NewsFeedComponent/LinkPreview";
import { Delete, Document, EditSquare } from "react-iconly";
import SubPageHeader from "../../components/SubPageHeader";

const NewsFeed = () => {
  const clientId = useSelector(state => state.client ? state.client.id : mstore.getPath(["user", "company_id"]));

  const [selectedPostId, setSelectedPostId] = useState(null);
  const [postText, setPostText] = useState("");
  const [postTypeId, setPostTypeId] = useState("0");
  const [showAddLink, setShowAddLink] = useState(false);
  const [uploadImage, setUploadImage] = useState(false);
  const [uploadDoc, setUploadDoc] = useState(false);
  const [openPostDialog, setOpenPostDialog] = useState(false);
  const [openImagePreviewModal, setOpenImagePreviewModal] = useState(false);
  const [showPreviewLinkBlock, setShowPreviewLinkBlock] = useState(true);
  const [showMyPost, setShowMyPost] = useState(false);
  const [attachedLink, setAttachedLink] = useState("");
  const [ogImageUrl, setOgImageUrl] = useState("");
  const [ogTitle, setOgTitle] = useState("");
  const [ogDescription, setOgDescription] = useState("");
  const [attachedPics, setAttachedPics] = useState([]);
  const [attachedDoc, setAttachedDoc] = useState([]);
  const [selectedPics, setSelectedPics] = useState([]);
  const [postList, setPostList] = useState([]);
  const [uploadImageName, setUploadImageName] = useState("Choose an image");
  const [uploadDocName, setUploadDocName] = useState("Choose file...");
  const [hasMore, setHasMore] = useState(true);
  const [postDeleting, setPostDeleting] = useState(false);
  const [postUploading, setPostUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Track previous openPostDialog state
  const prevOpenPostDialog = useRef(openPostDialog);

  // On mount or clientId change, show all posts
  useEffect(() => {
    checkAccess(clientId);
    setShowMyPost(false);
    setPostList([]);
    setHasMore(true);
    fetchPosts(true);
    // eslint-disable-next-line
  }, [clientId]);

  // When toggling between all/my posts, clear and fetch
  useEffect(() => {
    setPostList([]);
    setHasMore(true);
    if (showMyPost) {
      fetchMyPosts(true);
    } else {
      fetchPosts(true);
    }
    // eslint-disable-next-line
  }, [showMyPost]);

  useEffect(() => {
    if (prevOpenPostDialog.current && !openPostDialog) {
      if (showMyPost) {
        fetchMyPosts(true);
      } else {
        fetchPosts(true);
      }
    }
    prevOpenPostDialog.current = openPostDialog;
    // eslint-disable-next-line
  }, [openPostDialog]);

  // Fetch all posts
  const fetchPosts = useCallback((reset = false) => {
    fetchWithJson(endpoint.fetchPosts, {
      limit: 10,
      offset: reset ? 0 : postList.length,
    })
      .then((res) => res.json())
      .then((data) => {
        setPostList(prev => reset ? data.postList : [...prev, ...data.postList]);
        setHasMore(data.postList.length >= 5);
      })
      .catch(() => error("Unable to fetch news feed posts"));
  }, [postList.length]);

  // Fetch my posts
  const fetchMyPosts = useCallback((reset = false) => {
    fetchWithJson(endpoint.fetchMyPosts, {
      limit: 10,
      offset: reset ? 0 : postList.length,
    })
      .then((res) => res.json())
      .then((data) => {
        setPostList(prev => reset ? data.postList : [...prev, ...data.postList]);
        setHasMore(data.postList.length >= 5);
      })
      .catch(() => error("Unable to fetch news feed posts"));
  }, [postList.length]);

  // State setters for PostUploadModal
  const setValueInState = (stateName, value) => {
    switch (stateName) {
      case "postText": setPostText(value); break;
      case "postTypeId": setPostTypeId(value); break;
      case "showAddLink": setShowAddLink(value); break;
      case "uploadImage": setUploadImage(value); break;
      case "uploadDoc": setUploadDoc(value); break;
      case "attachedLink": setAttachedLink(value); break;
      case "ogImageUrl": setOgImageUrl(value); break;
      case "ogTitle": setOgTitle(value); break;
      case "ogDescription": setOgDescription(value); break;
      case "attachedPics": setAttachedPics(value); break;
      case "attachedDoc": setAttachedDoc(value); break;
      case "uploadImageName": setUploadImageName(value); break;
      case "uploadDocName": setUploadDocName(value); break;
      default: break;
    }
  };

  const onCancel = () => {
    setOpenPostDialog(false);
    setOpenImagePreviewModal(false);
  };

  const openEditModal = (post) => {
    setOpenPostDialog(true);
    setPostText(post.body || "");
    setAttachedDoc(post.docArray);
    setAttachedPics(post.imageArray);
    setAttachedLink(post.hyperlink || "");
    setSelectedPostId(post.id);
  };

  const removeAttachedPic = (pic) => {
    setAttachedPics(prev => prev.filter(item => item.id !== pic.id));
  };

  const removeAttachedDoc = (doc) => {
    setAttachedDoc(prev => prev.filter(item => item.id !== doc.id));
  };

  const closePreviewLink = () => setShowPreviewLinkBlock(false);

  const triggerUploadAndUpdate = async () => {
    setPostUploading(true);
    fetchWithJson(endpoint.fetchUrlMetadata, { url: attachedLink })
      .then((res) => res.json())
      .then((data) => {
        setOgTitle(data.metadata ? data.metadata.ogTitle || "" : "");
        setOgDescription(data.metadata ? data.metadata.ogDescription || "" : "");
        setOgImageUrl(
          data.metadata
            ? data.metadata.ogImage
              ? data.metadata.ogImage[0]
                ? data.metadata.ogImage[0].url
                : data.metadata.ogImage.url
              : ""
            : ""
        );
        if (showMyPost) {
          updatePost();
        } else {
          uploadPost();
        }
      })
      .catch((err) => {
        console.log(err);
        setOgImageUrl("");
        setOgDescription("");
        setOgImageUrl("");
        if (showMyPost) {
          updatePost();
        } else {
          uploadPost();
        }
      });
  };

  const uploadPost = () => {
    if (clientId === 1 && postTypeId === "0") {
      error("Please select post type id");
      setPostUploading(false);
      return;
    }
    if (
      attachedPics.length === 0 &&
      attachedDoc.length === 0 &&
      !postText &&
      !attachedLink
    ) {
      error("Please add any item to post");
      setPostUploading(false);
      return;
    }
    fetchWithJson(endpoint.uploadPost, {
      postTypeId: clientId === 1 ? postTypeId : 3,
      title: "",
      attachedPics,
      attachedDoc,
      postText,
      attachedLink,
      ogImageUrl,
      ogTitle,
      ogDescription,
    })
      .then((res) => res.json())
      .then(() => {
        success("You have successfully uploaded the post");
        setPostUploading(false);
        onCancel();
      })
      .catch((err) => {
        console.log(err);
        setPostUploading(false);
        error("Something went wrong during upload post");
      });
  };

  const updatePost = () => {
    if (
      attachedPics.length === 0 &&
      attachedDoc.length === 0 &&
      !postText &&
      !attachedLink
    ) {
      error("Please add any item to post");
      setPostUploading(false);
      return;
    }
    fetchWithJson(endpoint.updatePost, {
      postId: selectedPostId,
      title: "",
      attachedPics,
      attachedDoc,
      postText,
      attachedLink,
      ogImageUrl,
      ogTitle,
      ogDescription,
    })
      .then((res) => res.json())
      .then(() => {
        success("You have successfully updated the post");
        setPostUploading(false);
        onCancel();
      })
      .catch((err) => {
        console.log(err);
        setPostUploading(false);
        error("Something went wrong during update post");
      });
  };

  const deletePost = (post) => {
    setPostDeleting(true);
    fetchWithJson(endpoint.deletePost, {
      postId: post.id
    })
      .then((res) => res.json())
      .then(() => {
        success("You have successfully removed the post");
        setPostDeleting(false);
        onCancel();
      })
      .catch((err) => {
        console.log(err);
        setPostDeleting(false);
        error("Something went wrong during remove post");
      });
  };

  // Post upload modal
  const postUploadModal = () => (
    <Modal
      blur
      css={{ br: 0 }}
      open={openPostDialog}
      onClose={onCancel}
      closeButton
      width="800px"
    >
      <Modal.Header><Text size={18}>Create a post</Text></Modal.Header>
      <Modal.Body>
        <PostUploadModal
          setValueInState={setValueInState}
          postText={postText}
          toggleAddLink={() => setShowAddLink(v => !v)}
          toggleUploadImage={() => setUploadImage(v => !v)}
          toggleUploadDoc={() => setUploadDoc(v => !v)}
          showAddLink={showAddLink}
          attachedLink={attachedLink}
          uploadImage={uploadImage}
          attachedPics={attachedPics}
          uploadImageName={uploadImageName}
          uploadDoc={uploadDoc}
          attachedDoc={attachedDoc}
          uploadDocName={uploadDocName}
          showPreviewLinkBlock={showPreviewLinkBlock}
          closePreviewLink={closePreviewLink}
          removeAttachedPic={removeAttachedPic}
          removeAttachedDoc={removeAttachedDoc}
          uploadPost={uploadPost}
          postUploading={postUploading}
          showMyPost={showMyPost}
          updatePost={updatePost}
          postTypeId={postTypeId}
          triggerUploadAndUpdate={triggerUploadAndUpdate}
          deletePost={deletePost}
          postDeleting={postDeleting}
        />
      </Modal.Body>
    </Modal>
  );

  // Image preview modal
  const imagePreviewModal = () => (
    <Modal
      width="800px"
      height="640px"
      blur
      css={{ br: 0 }}
      open={openImagePreviewModal}
      onClose={onCancel}
      closeButton
    >
      <Modal.Header>
        <Text size={18}>Image preview</Text>
      </Modal.Header>
      <Modal.Body>
        <div className="pa3 overflow-y-scroll">
          {selectedPics.map((item) => (
            <img
              key={item.id}
              style={{
                marginRight: 10,
                objectFit: "contain",
                borderRadius: 5,
                marginTop: 5,
              }}
              className="w-100 bg-white"
              src={item.url}
              alt={item.name}
              title={item.name}
            />
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );

  // Post image preview
  const postImagePreview = (pics) => {
    if (pics.length === 1) {
      return (
        <div
          className="flex flex-column w-100 mt3"
          onClick={() => {
            setSelectedPics(pics);
            setOpenImagePreviewModal(true);
          }}
        >
          <div className="flex w-100 pa2" style={{ borderRadius: 5 }}>
            <img
              style={{
                height: 320,
                width: "100%",
                marginRight: 10,
                objectFit: "cover",
              }}
              className="bg-white-40 mr2"
              src={pics[0].url}
              alt={pics[0].name}
              title={pics[0].name}
            />
          </div>
        </div>
      );
    } else if (pics.length === 2) {
      return (
        <div
          className="flex flex-column w-100 mt3"
          onClick={() => {
            setSelectedPics(pics);
            setOpenImagePreviewModal(true);
          }}
        >
          <div className="flex w-100 pa2" style={{ borderRadius: 5 }}>
            <img
              style={{
                height: 320,
                width: "50%",
                marginRight: 10,
                objectFit: "cover",
              }}
              className="bg-white-40 mr2"
              src={pics[0].url}
              alt={pics[0].name}
              title={pics[0].name}
            />
            <img
              style={{
                height: 320,
                width: "50%",
                marginRight: 10,
                objectFit: "cover",
              }}
              className="bg-white-40"
              src={pics[1].url}
              alt={pics[1].name}
              title={pics[1].name}
            />
          </div>
        </div>
      );
    } else {
      return (
        <div
          className="flex flex-column w-100 mt3"
          onClick={() => {
            setSelectedPics(pics);
            setOpenImagePreviewModal(true);
          }}
        >
          <div className="flex w-100 pa2" style={{ borderRadius: 5 }}>
            <img
              style={{
                height: 320,
                width: "60%",
                marginRight: 10,
                objectFit: "cover",
              }}
              className="bg-white-40 mr2"
              src={pics[0].url}
              alt={pics[0].name}
              title={pics[0].name}
            />
            <div className="flex flex-column w-2/5">
              <img
                style={{
                  height: 156,
                  width: "100%",
                  marginRight: 10,
                  objectFit: "cover",
                }}
                className="mb2 bg-white-40"
                src={pics[1].url}
                alt={pics[1].name}
                title={pics[1].name}
              />
              <img
                style={{
                  height: 156,
                  width: "100%",
                  marginRight: 10,
                  objectFit: "cover",
                }}
                className="bg-white-40"
                src={pics[2].url}
                alt={pics[2].name}
                title={pics[2].name}
              />
            </div>
          </div>
        </div>
      );
    }
  };

  // Post doc preview
  const postDocPreview = (docs) => (
    <div className="flex w-100 mt3 flex-wrap">
      {docs.map((item) => (
        <a
          key={item.id}
          href={item.url}
          style={{
            textDecoration: "none",
            color: "rgba( 0, 0, 0, .8 )",
          }}
        >
          <div
            className="pointer bg-black-20 ma2 ph3 flex justify-center items-center"
            style={{
              height: 40,
              borderRadius: 20,
            }}
          >
            <Document set="light" primaryColor="blueviolet" />
            <div className="tc f5 ml2">{item.originalName}</div>
          </div>
        </a>
      ))}
    </div>
  );

  const timelineView = (item) => (
    <div 
      className="w-50-l w-100 mt3 pa4 br3 bg-white shadow-lg hover:shadow-md transition-shadow duration-200" 
      key={item.id}
    >
      {/* Header Section */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <Text h4 className="mb-1">{item.companyName}</Text>
          <Text size={14} color="$gray600">{item.post_type_desc}</Text>
        </div>
        <div className="flex items-center gap-2">
          <Text h4 color="$gray600">{item.createdOn}</Text>
          {showMyPost && (
            <div className="flex gap-2 ml-4">
              <Button
                light
                auto
                size="sm"
                icon={<EditSquare size={20} set='light' />}
                onClick={() => openEditModal(item)}
                css={{ minWidth: 'auto', p: 0 }}
              />
              <Button
                light
                auto
                size="sm"
                icon={<Delete size={20} set="light" primaryColor="red" />}
                onClick={() => deletePost(item)}
                css={{ minWidth: 'auto', p: 0 }}
              />
            </div>
          )}
        </div>
      </div>
      
      <div className="divider"></div>

      {/* Content Section */}
      <div className="space-y-4">
        {/* Text Content */}
        {item.body && (
          <Text className="text-gray-800 leading-relaxed">
            {item.body}
          </Text>
        )}
  
        {/* Link Preview */}
        {item.hyperlink && (
          <div className="mt-3">
            <LinkPreview
              showClose={false}
              closePreviewLink={closePreviewLink}
              ogTitle={item.previewTitle}
              ogImageUrl={item.previewImage}
              ogDescription={item.previewDesc}
              link={item.hyperlink}
            />
          </div>
        )}
  
        {/* Image Gallery */}
        {item.imageArray.length > 0 && (
          <div className="mt-3">
            {postImagePreview(item.imageArray)}
          </div>
        )}
  
        {/* Document Attachments */}
        {item.docArray.length > 0 && (
          <div className="mt-3">
            {postDocPreview(item.docArray)}
          </div>
        )}
      </div>
    </div>
  );

  // Render
  if (showMyPost === 0 && !loading) {
    return (
      <div className="ml3 nt3">
        <Text h4>
          News Feed feature is unavailable
        </Text>
      </div>
    );
  }

  return (
    <div className="">
      <Row justify='space-between' className="p-4 border-y-2 border-dotted">
        <SubPageHeader>
          News Feed
        </SubPageHeader>
        <div className="flex">
          <div className="mr2">
            <Switch
              checked={showMyPost}
              onChange={() => setShowMyPost(v => !v)}
            />
          </div>
          <Text size={16} b css={{ py: '$2' }}>My posts</Text>
          <Button
            css={{ br: 0, ml: 20 }}
            auto
            size='sm'
            onClick={() => {
              setOpenPostDialog(true);
              setAttachedLink("");
              setPostText("");
              setShowMyPost(false);
              setUploadImageName("(click here to open up the file browser)");
              setUploadDocName("(click here to open up the file browser)");
              setSelectedPostId(null);
              setAttachedDoc([]);
              setAttachedPics([]);
              setPostList([]);
              setHasMore(true);
              // fetchPosts(); // <-- REMOVED!
            }}
          >
            New Post
          </Button>
        </div>
      </Row>

      <InfiniteScroll
        dataLength={postList.length}
        next={showMyPost ? fetchMyPosts : fetchPosts}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: 'calc(100vh - 100px)',
        }}
        hasMore={hasMore}
        endMessage={
          <p className="tc mt2">
            <b>Yay! You have seen it all</b>
          </p>
        }
        loader={<h4 className="mt2">Loading...</h4>}
      >
        {postList.length > 0
          ? postList.map((item) => timelineView(item))
          : null}
      </InfiniteScroll>

      {postUploadModal()}
      {imagePreviewModal()}
    </div>
  );
};

export default NewsFeed;